import { useState } from "react";
import { AlertTriangle, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  runBriefDraftPass,
  runBriefPublicationPrep,
  runPublicationDispatch,
  type ContentBrief,
  type ContentBriefDraft,
  type ContentBriefPlan,
  type ContentBriefPublication,
  type ContentPublishingDispatch,
  type ContentDestinationFormat,
} from "@/lib/composerCore";

type FailureKind = "plan" | "draft" | "publication" | "dispatch";

export type BriefFailure = {
  kind: FailureKind;
  label: string;
  message: string;
  occurredAt: string;
  retry: () => Promise<void>;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | null;
  brief: ContentBrief | null;
  latestPlan: ContentBriefPlan | null;
  latestDraft: ContentBriefDraft | null;
  failedPublications: ContentBriefPublication[];
  failedDispatches: ContentPublishingDispatch[];
  onRetried: () => Promise<void> | void;
};

const friendlyKindLabel: Record<FailureKind, string> = {
  plan: "Planning pass",
  draft: "Drafting pass",
  publication: "Publication prep",
  dispatch: "Dispatch",
};

export const BriefErrorDialog = ({
  open,
  onOpenChange,
  workspaceId,
  brief,
  latestPlan,
  latestDraft,
  failedPublications,
  failedDispatches,
  onRetried,
}: Props) => {
  const [retryingId, setRetryingId] = useState<string | null>(null);

  if (!brief) return null;

  const failures: BriefFailure[] = [];

  // Planning failures are handled inline by the conversational
  // BriefIntakePanel (Phase B2c) — no single-shot retry surface here.
  void latestPlan;

  if (workspaceId && latestDraft?.status === "failed") {
    failures.push({
      kind: "draft",
      label: friendlyKindLabel.draft,
      message: latestDraft.error_message || "Draft generation failed without a server message.",
      occurredAt: latestDraft.completed_at || latestDraft.updated_at,
      retry: async () => {
        await runBriefDraftPass(workspaceId, {
          briefId: brief.id,
          planId: latestDraft.plan_id ?? null,
        });
      },
    });
  }

  for (const pub of failedPublications) {
    if (!workspaceId) break;
    failures.push({
      kind: "publication",
      label: `${friendlyKindLabel.publication} · ${pub.destination_format}`,
      message: pub.error_message || "Publication preparation failed without a server message.",
      occurredAt: pub.completed_at || pub.updated_at,
      retry: async () => {
        await runBriefPublicationPrep(workspaceId, {
          briefId: brief.id,
          draftId: pub.draft_id,
          destinationFormat: pub.destination_format as ContentDestinationFormat,
        });
      },
    });
  }

  for (const dispatch of failedDispatches) {
    if (!workspaceId || !dispatch.publication_id || !dispatch.connector_id) continue;
    failures.push({
      kind: "dispatch",
      label: friendlyKindLabel.dispatch,
      message: dispatch.error_message || "Dispatch failed without a provider message.",
      occurredAt: dispatch.completed_at || dispatch.updated_at,
      retry: async () => {
        await runPublicationDispatch(workspaceId, {
          publicationId: dispatch.publication_id!,
          connectorId: dispatch.connector_id!,
          allowStale: true,
        });
      },
    });
  }

  const handleRetry = async (id: string, failure: BriefFailure) => {
    setRetryingId(id);
    try {
      await failure.retry();
      toast.success(`Retried ${failure.label.toLowerCase()}`);
      await onRetried();
      onOpenChange(false);
    } catch (error) {
      toast.error((error as Error).message ?? "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            {brief.title}: needs attention
          </DialogTitle>
          <DialogDescription>
            One or more pipeline steps failed. Review the messages below and retry once you've addressed the cause.
          </DialogDescription>
        </DialogHeader>

        {failures.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active failures recorded for this brief.</p>
        ) : (
          <ul className="space-y-3">
            {failures.map((failure, index) => {
              const id = `${failure.kind}:${index}`;
              const isRetrying = retryingId === id;
              return (
                <li key={id} className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-destructive/40 text-destructive">
                      {failure.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {new Date(failure.occurredAt).toLocaleString()}
                    </span>
                    <div className="ml-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleRetry(id, failure)}
                        disabled={retryingId !== null}
                      >
                        {isRetrying ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCw className="h-3.5 w-3.5" />
                        )}
                        Retry
                      </Button>
                    </div>
                  </div>
                  <p className="mt-2 break-words text-sm text-foreground">{failure.message}</p>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={retryingId !== null}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BriefErrorDialog;
